import { Box, Button, IconButton, Typography, keyframes } from "@mui/material";
// WebP from the PNGs beside them (scripts/optimize-home-images.mjs). The balls
// stay PNG: they are small enough that the build inlines them.
import FloatingCharacter from "../../../assets/images/floatmansplit/character.webp";
import BubbleLayer2 from "../../../assets/images/floatmansplit/Layer2.webp";
import BubbleLayer3 from "../../../assets/images/floatmansplit/Layer3.webp";
import BubbleLayer4 from "../../../assets/images/floatmansplit/Layer4.webp";
import BubbleLayer5 from "../../../assets/images/floatmansplit/Layer5.webp";
import Ball1 from "../../../assets/images/floatmansplit/Ball1.png";
import Ball2 from "../../../assets/images/floatmansplit/Ball2.png";
import Ball3 from "../../../assets/images/floatmansplit/Ball3.png";
// Legacy white-blob backgrounds (restored from the original neramClasses section)
import CurveBlob from "../../../assets/images/SVGs/bottom-curve.svg"; // desktop, full-bleed right
import MobileBlob from "../../../assets/images/SVGs/backup-curve-blob.svg"; // mobile, behind text
import { Instagram, YouTube } from "@mui/icons-material";
import { useAmbient } from "../../../components/Motion/useAmbient";
import { useReveal } from "../../../components/Motion";

// Main character floating animation
const floatCharacter = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
`;

// Shadow animation - synced inversely with character
const shadowPulse = keyframes`
  0%, 100% {
    transform: translateX(-50%) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translateX(-50%) scale(0.75);
    opacity: 0.5;
  }
`;

// Bubble floating animations - each with unique movement pattern
const floatBubble1 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(8px, -10px) rotate(3deg); }
  66% { transform: translate(-5px, -6px) rotate(-2deg); }
`;

const floatBubble2 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(-6px, -8px) rotate(-2deg); }
  75% { transform: translate(4px, -12px) rotate(2deg); }
`;

const floatBubble3 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  40% { transform: translate(10px, -7px) rotate(4deg); }
  70% { transform: translate(-3px, -10px) rotate(-1deg); }
`;

const floatBubble4 = keyframes`
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  30% { transform: translate(-8px, -5px) rotate(-3deg); }
  60% { transform: translate(6px, -9px) rotate(2deg); }
`;

const floatBall1 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(5px, -8px); }
`;

const floatBall2 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-6px, -10px); }
`;

const floatBall3 = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(4px, -6px); }
`;

// Social bubble float - gentle bob + tilt, echoing the floating scene bubbles
const floatSocial1 = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-7px) rotate(-4deg); }
`;

const floatSocial2 = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(4deg); }
`;

// Sparkle stars twinkling around the social bubbles
const sparkleFlash = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.4); }
  50% { opacity: 1; transform: scale(1.1); }
`;

// Two playful, floating brand bubbles (YouTube + Instagram).
// Rendered in the header on desktop and in the CTA row on mobile.
const SocialBubbles = () => (
  <>
    {/* YouTube - glossy red bubble that floats + twinkles */}
    <IconButton
      component="a"
      href="https://www.youtube.com/c/Neram"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="YouTube"
      sx={{
        position: "relative",
        overflow: "visible",
        width: 42,
        height: 42,
        color: "#fff",
        background: "linear-gradient(135deg, #ff5b5b 0%, #ff0000 100%)",
        boxShadow: "0 8px 18px rgba(255,0,0,0.35)",
        animation: `${floatSocial1} 4s ease-in-out infinite`,
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          background: "linear-gradient(135deg, #ff0000, #cc0000)",
          transform: "translateY(-4px) scale(1.1)",
          boxShadow: "0 14px 26px rgba(255,0,0,0.5)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 7,
          left: 10,
          width: 13,
          height: 8,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.6)",
          filter: "blur(1px)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '"✦"',
          position: "absolute",
          top: -7,
          right: -5,
          fontSize: "0.7rem",
          color: "#ff9bb5",
          opacity: 0,
          animation: `${sparkleFlash} 3.4s ease-in-out infinite`,
          animationDelay: "0.3s",
          pointerEvents: "none",
        },
      }}
    >
      <YouTube sx={{ fontSize: "20px", position: "relative", zIndex: 1 }} />
    </IconButton>

    {/* Instagram - glossy gradient bubble, floats out of sync */}
    <IconButton
      component="a"
      href="https://www.instagram.com/neramclassrooms/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Instagram"
      sx={{
        position: "relative",
        overflow: "visible",
        width: 42,
        height: 42,
        color: "#fff",
        background:
          "linear-gradient(45deg, #feda75, #fa7e1e, #d62976, #962fbf, #4f5bd5)",
        boxShadow: "0 8px 18px rgba(214,41,118,0.38)",
        animation: `${floatSocial2} 5s ease-in-out 0.6s infinite`,
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px) scale(1.1)",
          boxShadow: "0 14px 26px rgba(214,41,118,0.55)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 7,
          left: 10,
          width: 13,
          height: 8,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.55)",
          filter: "blur(1px)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '"✦"',
          position: "absolute",
          bottom: -6,
          left: -6,
          fontSize: "0.65rem",
          color: "#c89bff",
          opacity: 0,
          animation: `${sparkleFlash} 3.8s ease-in-out infinite`,
          animationDelay: "1.6s",
          pointerEvents: "none",
        },
      }}
    >
      <Instagram sx={{ fontSize: "20px", position: "relative", zIndex: 1 }} />
    </IconButton>
  </>
);

const NeramSection = () => {
  // Thirteen looping float/sparkle animations live in here; pause them offscreen.
  const ambientRef = useAmbient();

  // The scene reveals as one piece: its bubbles and balls already run their own
  // transform loops, so the reveal goes on the container rather than on them.
  // The text column's pieces arrive in reading order.
  const sceneReveal = useReveal();
  const headerReveal = useReveal();
  const descriptionReveal = useReveal();
  const statReveal = useReveal();
  const ctaReveal = useReveal();

  return (
    <Box
      ref={ambientRef}
      data-nav-bg="light"
      sx={{
        width: "100%",
        position: "relative",
        // Clip only the horizontal bleed (no sideways scrollbar) while letting
        // the white blob overflow vertically and flow, uncut, behind the
        // sections above (Hero) and below (AboutMe). The blob sits at z-index -1
        // so it stays behind their content.
        overflowX: "clip",
        display: "flex",
        justifyContent: "center",
        mt: { xs: 4, md: 10 },
        py: { xs: 4, md: 5 },
        px: { xs: 2, md: 3 },
      }}
    >
      {/* Legacy white blob - full-bleed off the right edge (desktop only) */}
      <Box
        component="img"
        src={CurveBlob}
        alt=""
        aria-hidden
        sx={{
          position: "absolute",
          top: "50%",
          right: 0,
          transform: "translateY(-50%)",
          width: { md: "72vw", lg: "62vw", xl: "56vw" },
          minHeight: "100%",
          height: "auto",
          // Behind the neighbouring sections (FeaturedWork / AboutMe) so the
          // blob's top & bottom curves flow past this section unclipped.
          zIndex: -1,
          pointerEvents: "none",
          display: { xs: "none", md: "block" },
        }}
      />

      {/* Centered Container */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "1000px",
          display: "flex",
          // Mobile: text (in the blob) on top, floating scene below - per the
          // legacy mobile layout. Desktop: scene left, text right.
          flexDirection: { xs: "column-reverse", md: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: 2, md: 4 },
          color: "rgba(0,0,0,0.85)",
        }}
      >
        {/* Floating Scene Container */}
        <Box
          ref={sceneReveal.ref}
          {...sceneReveal.props}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            minHeight: { xs: "350px", md: "450px" },
            width: "100%",
            maxWidth: "450px",
          }}
        >
          {/* Bubble Layer 2 - Top Left */}
          <Box
            component="img"
            src={BubbleLayer2}
            alt=""
            sx={{
              position: "absolute",
              top: "5%",
              left: "12%",
              width: { xs: "65px", md: "85px" },
              height: "auto",
              animation: `${floatBubble1} 4s ease-in-out infinite`,
              animationDelay: "0.2s",
            }}
          />

          {/* Bubble Layer 3 - Top Center */}
          <Box
            component="img"
            src={BubbleLayer3}
            alt=""
            sx={{
              position: "absolute",
              top: "0%",
              left: "30%",
              width: { xs: "55px", md: "70px" },
              height: "auto",
              animation: `${floatBubble2} 3.5s ease-in-out infinite`,
              animationDelay: "0.5s",
            }}
          />

          {/* Bubble Layer 4 - Top Right */}
          <Box
            component="img"
            src={BubbleLayer4}
            alt=""
            sx={{
              position: "absolute",
              top: "2%",
              right: "28%",
              width: { xs: "60px", md: "80px" },
              height: "auto",
              animation: `${floatBubble3} 4.5s ease-in-out infinite`,
              animationDelay: "0.1s",
            }}
          />

          {/* Bubble Layer 5 - Upper Right */}
          <Box
            component="img"
            src={BubbleLayer5}
            alt=""
            sx={{
              position: "absolute",
              top: "18%",
              right: "8%",
              width: { xs: "55px", md: "70px" },
              height: "auto",
              animation: `${floatBubble4} 3.8s ease-in-out infinite`,
              animationDelay: "0.3s",
            }}
          />

          {/* Ball 1 Shadow - Bottom Left */}
          <Box
            sx={{
              position: "absolute",
              bottom: "9%",
              left: "16%",
              width: { xs: "18px", md: "22px" },
              height: { xs: "6px", md: "8px" },
              borderRadius: "50%",
              // Softened in the gradient itself rather than with filter: blur,
              // which on an animating element is a GPU filter pass every frame.
              background: "radial-gradient(ellipse closest-side, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.14) 60%, transparent 100%)",
              animation: `${floatBall1} 3s ease-in-out infinite`,
            }}
          />

          {/* Ball 1 - Bottom Left */}
          <Box
            component="img"
            src={Ball1}
            alt=""
            sx={{
              position: "absolute",
              bottom: "12%",
              left: "15%",
              width: { xs: "22px", md: "28px" },
              height: "auto",
              animation: `${floatBall1} 3s ease-in-out infinite`,
            }}
          />

          {/* Ball 2 - Top Left Area */}
          <Box
            component="img"
            src={Ball2}
            alt=""
            sx={{
              position: "absolute",
              top: "8%",
              left: "5%",
              width: { xs: "28px", md: "35px" },
              height: "auto",
              animation: `${floatBall2} 3.2s ease-in-out infinite`,
              animationDelay: "0.4s",
            }}
          />

          {/* Ball 3 Shadow - Bottom Right */}
          <Box
            sx={{
              position: "absolute",
              bottom: "15%",
              right: "13%",
              width: { xs: "14px", md: "18px" },
              height: { xs: "5px", md: "6px" },
              borderRadius: "50%",
              // Softened in the gradient itself rather than with filter: blur,
              // which on an animating element is a GPU filter pass every frame.
              background: "radial-gradient(ellipse closest-side, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.14) 60%, transparent 100%)",
              animation: `${floatBall3} 2.8s ease-in-out infinite`,
              animationDelay: "0.6s",
            }}
          />

          {/* Ball 3 - Bottom Right */}
          <Box
            component="img"
            src={Ball3}
            alt=""
            sx={{
              position: "absolute",
              bottom: "18%",
              right: "12%",
              width: { xs: "18px", md: "24px" },
              height: "auto",
              animation: `${floatBall3} 2.8s ease-in-out infinite`,
              animationDelay: "0.6s",
            }}
          />

          {/* Main Character Shadow */}
          <Box
            sx={{
              position: "absolute",
              bottom: "5%",
              left: "50%",
              width: { xs: "120px", md: "160px" },
              height: { xs: "25px", md: "35px" },
              borderRadius: "50%",
              background: "radial-gradient(ellipse closest-side, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.26) 45%, rgba(0,0,0,0.08) 78%, transparent 100%)",
              animation: `${shadowPulse} 3s ease-in-out infinite`,
            }}
          />

          {/* Main Character */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              animation: `${floatCharacter} 3s ease-in-out infinite`,
            }}
          >
            <img
              src={FloatingCharacter}
              alt="Neram Classes - EdTech Platform"
              style={{
                width: "100%",
                maxWidth: "320px",
                display: "block",
                margin: 0,
              }}
            />
          </Box>
        </Box>

        {/* Text Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            textAlign: "left",
            width: "100%",
            maxWidth: { md: "480px" },
            // Mobile: the text sits INSIDE the legacy white "cloud" blob
            // (backup-curve-blob.svg), stretched to wrap the content. Desktop
            // uses the full-bleed blob behind the whole section instead.
            backgroundImage: { xs: `url(${MobileBlob})`, md: "none" },
            backgroundRepeat: "no-repeat",
            backgroundSize: { xs: "100% 100%", md: "auto" },
            px: { xs: "11%", md: 0 },
            py: { xs: "13%", md: 0 },
          }}
        >
          {/* Header with neram heading + (desktop) social bubbles */}
          <Box
            ref={headerReveal.ref}
            {...headerReveal.props}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Kalam, cursive",
                  fontSize: "14px",
                  fontStyle: "italic",
                  color: "rgba(0,0,0,0.55)",
                  mb: 0.5,
                }}
              >
                Founder of
              </Typography>
              <a
                href="https://neramclasses.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: { xs: "22px", md: "26px" },
                    fontWeight: 400,
                    lineHeight: 1.2,
                    color: "#111",
                  }}
                >
                  <strong>neram</strong>Classes
                  <strong style={{ color: "#ea4c89" }}>.com</strong>
                </Typography>
              </a>
            </Box>

            {/* Desktop: social bubbles live in the header, top-right */}
            <Box
              sx={{ display: { xs: "none", md: "flex" }, gap: 1.5, pt: 0.5 }}
            >
              <SocialBubbles />
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "block" },
              borderBottom: "1px solid rgba(0,0,0,0.15)",
              mb: 2,
              width: "100%",
            }}
          />

          {/* Description */}
          <Typography
            ref={descriptionReveal.ref}
            {...descriptionReveal.props}
            sx={{
              fontFamily: "Kalam, cursive",
              fontSize: { xs: "14px", md: "16px" },
              lineHeight: 1.7,
              color: "rgba(40, 30, 70, 0.85)",
              mb: 2.5,
            }}
          >
            neramClasses.com is my idea. As a head of this startup I learned
            many skills apart from web design & development. neram is a self
            learning platform for design based competitive exams in India.
          </Typography>

          {/* Traction stat - Cloudflare-verified monthly reach */}
          <Box
            ref={statReveal.ref}
            {...statReveal.props}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 2.5,
              px: 1.75,
              py: 0.75,
              borderRadius: "999px",
              background: "rgba(35,213,171,0.12)",
              border: "1px solid rgba(35,213,171,0.35)",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "15px", md: "16px" },
                fontWeight: 700,
                color: "#17a98a",
                lineHeight: 1,
              }}
            >
              168K+
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "11px", md: "12px" },
                fontWeight: 500,
                color: "rgba(40,30,70,0.7)",
                lineHeight: 1,
              }}
            >
              monthly pageviews
            </Typography>
          </Box>

          {/* CTA: Website button + (mobile) social bubbles share one row */}
          <Box
            ref={ctaReveal.ref}
            {...ctaReveal.props}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <a
              href="https://neramclasses.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #ea4c89, #e73c7e)",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 600,
                  px: 3,
                  py: 0.875,
                  boxShadow: "0 6px 16px rgba(231,60,126,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #e73c7e, #ea4c89)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 22px rgba(231,60,126,0.45)",
                  },
                  transition: "background 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease",
                }}
              >
                Website
              </Button>
            </a>

            {/* Mobile: social bubbles sit beside the Website button */}
            <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1.5 }}>
              <SocialBubbles />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NeramSection;
